import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "../../CSS/CustomerCareCRM.css";
import api from "../../api.js";
import { useRights } from "../../Components/RightsContext.jsx";

/* ─── Still hardcoded on purpose (no master table for these) ─── */
// const CATS = {
//   delay: { label: "Delivery Delay", bg: "color-mix(in srgb, var(--warn) 15%, transparent)", fg: "var(--warn)" },
//   damage: { label: "Damaged Goods", bg: "color-mix(in srgb, var(--danger) 15%, transparent)", fg: "var(--danger)" },
//   lost: { label: "Lost Shipment", bg: "color-mix(in srgb, var(--danger) 15%, transparent)", fg: "var(--danger)" },
//   wrong: { label: "Wrong Delivery", bg: "color-mix(in srgb, var(--warn) 15%, transparent)", fg: "var(--warn)" },
//   billing: { label: "Billing Dispute", bg: "color-mix(in srgb, #7c3aed 15%, transparent)", fg: "#7c3aed" },
//   pod: { label: "POD Request", bg: "color-mix(in srgb, var(--accent2) 15%, transparent)", fg: "var(--accent2)" },
//   pickup: { label: "Pickup Request", bg: "color-mix(in srgb, var(--success) 15%, transparent)", fg: "var(--success)" },
//   track: { label: "Tracking Issue", bg: "color-mix(in srgb, var(--accent2) 15%, transparent)", fg: "var(--accent2)" },
//   rate: { label: "Rate Query", bg: "var(--panel)", fg: "var(--muted)" },
//   general: { label: "General Query", bg: "var(--panel)", fg: "var(--muted)" },
// };

const ticket_PATH = "Ticket/CustomerCareCRM";
const STATUSES = {
  open: { label: "Open", cls: "care-st-open" },
  progress: { label: "In Progress", cls: "care-st-progress" },
  pending: { label: "Pending Customer", cls: "care-st-pending" },
  resolved: { label: "Resolved", cls: "care-st-resolved" },
  closed: { label: "Closed", cls: "care-st-closed" },
};

const CANNED = [
  "Thank you for reaching out. We are looking into this and will update you shortly.",
  "Your shipment is in transit and on schedule for delivery. We will share tracking updates.",
  "We sincerely apologise for the inconvenience caused. This has been escalated on priority.",
  "Could you please confirm the consignment number and delivery address?",
  "The POD has been emailed to your registered address. Please check and confirm.",
];

// Your Priority master (mode=PRI) has no SLA-hours column yet, so SLA
// targets are mapped here by Priority_Value until that's added server-side.
const SLA_HOURS_BY_PRIORITY = { urgent: 4, high: 8, medium: 24, low: 48 };
function slaHoursFor(priorityValue) {
  const key = (priorityValue || "").trim().toLowerCase();
  return SLA_HOURS_BY_PRIORITY[key] ?? 24;
}

const H = 3600e3, D = 86400e3;

/* ─── HELPERS ───────────────────────────────────────────── */
function initials(n) { return (n || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase(); }
function pad(n) { return String(n).padStart(2, "0"); }
function fmtDT(d) { d = new Date(d); return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; }
function relTime(d, now) {
  d = new Date(d);
  const diff = now - d, a = Math.abs(diff), fut = diff < 0;
  let s;
  if (a < 60 * 1000) s = "just now";
  else if (a < H) s = Math.round(a / 60000) + "m";
  else if (a < D) s = Math.round(a / H) + "h";
  else s = Math.round(a / D) + "d";
  if (s === "just now") return s;
  return fut ? `in ${s}` : `${s} ago`;
}
function slaInfo(t, now) {
  const created = new Date(t.created_Date);
  const hours = slaHoursFor(t.priority_Value);
  const due = new Date(created.getTime() + hours * H);
  if (t.status === "resolved" || t.status === "closed")
    return { due, done: true, label: STATUSES[t.status]?.label || t.status, cls: "care-sla-ok", pct: 100 };
  const total = hours * H, used = now - created, pct = Math.max(0, Math.min(100, (used / total) * 100));
  if (now > due) {
    const over = relTime(due, now).replace(" ago", "");
    return { due, breach: true, label: "Overdue by " + over, cls: "care-sla-breach", pct: 100 };
  }
  const rem = relTime(due, now).replace("in ", "");
  return { due, label: "Due in " + rem, cls: pct > 75 ? "care-sla-warn" : "care-sla-ok", pct };
}

const TAB_DEFS = [["all", "All"], ["open", "Open"], ["progress", "In Progress"], ["pending", "Pending"], ["resolved", "Resolved"], ["closed", "Closed"]];
const NEW_TICKET_BLANK = {
  partyId: "", contact: "", phone: "", email: "", channel: "Phone",
  // category: Object.keys(CATS)[0], priorityId: "", agentId: "",
   category: "", priorityId: "", agentId: "",  
  cnrInput: "", consignmentId: null, route: "",
  subject: "", desc: "",
};

/* ─── SMALL PRESENTATIONAL BITS ─────────────────────────── */
/* ─── CareComboBox — searchable dropdown (same pattern as ComboBox in Branch.jsx) ───
   options    : [{ value, label }]
   emptyLabel : if given, adds a first option with value "" (e.g. "All Priority", "— Select —")
   onChange   : receives the picked value directly (not an event)                            */
function CareComboBox({ value, onChange, options = [], emptyLabel, placeholder = "Search…", disabled = false, hasError = false, wrapClass = "", small = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focIdx, setFocIdx] = useState(-1);
  const [dropStyle, setDropStyle] = useState({});
  const wrapRef = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const opts = useMemo(() => {
    const base = options.map(o => ({ value: String(o.value ?? ""), label: String(o.label ?? "") }));
    return emptyLabel != null ? [{ value: "", label: emptyLabel }, ...base] : base;
  }, [options, emptyLabel]);

  const cur = String(value ?? "");
  const selected = opts.find(o => o.value === cur);
  const filtered = query.trim() ? opts.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : opts;

  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
  };

  const closeBox = () => { setOpen(false); setQuery(""); setFocIdx(-1); };
  const openBox = () => {
    if (disabled || open) return;
    setOpen(true); setQuery(""); setFocIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const pick = (val) => { onChange(val); closeBox(); };

  const updatePos = useCallback(() => {
    if (!boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    setDropStyle({ position: "fixed", top: r.bottom + 4, left: r.left, width: Math.max(r.width, 160), zIndex: 99999 });
  }, []);

  // close on outside click (dropdown is portaled, so check both wrapper and list)
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      closeBox();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open || focIdx < 0 || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${focIdx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focIdx, open]);

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (focIdx >= 0 && filtered[focIdx]) pick(filtered[focIdx].value);
      else if (filtered.length === 1) pick(filtered[0].value);
    }
    else if (e.key === "Escape" || e.key === "Tab") closeBox();
  };

  return (
    <div className={`care-cb${wrapClass ? " " + wrapClass : ""}`} ref={wrapRef}>
      <div ref={boxRef}
        className={`care-cb-box${small ? " care-cb-sm" : ""}${open ? " open" : ""}${disabled ? " dis" : ""}${hasError ? " err" : ""}`}
        onClick={openBox}>
        <input
          ref={inputRef}
          className="care-cb-in"
          type="text"
          placeholder={selected?.label || placeholder}
          value={open ? query : (selected?.label || "")}
          onChange={e => { setQuery(e.target.value); setFocIdx(-1); }}
          onKeyDown={handleKey}
          onFocus={openBox}
          readOnly={!open}
          disabled={disabled}
          style={{ cursor: disabled ? "not-allowed" : open ? "text" : "pointer" }}
        />
        <span className="care-cb-chev">▾</span>
      </div>
      {open && createPortal(
        <div className="care-cb-drop" style={dropStyle} ref={listRef}>
          {filtered.length === 0
            ? <div className="care-cb-empty">No results found</div>
            : filtered.map((o, i) => (
              <div key={o.value + "_" + i} data-idx={i}
                className={`care-cb-opt${o.value === cur ? " sel" : ""}${i === focIdx ? " foc" : ""}`}
                onMouseDown={e => { e.preventDefault(); pick(o.value); }}>
                {highlight(o.label, query)}
              </div>
            ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function CareCatChip({ code, categories = [] }) {
  const c = categories.find(x => String(x.id) === String(code));
  return <span className="care-cat-chip">{c ? `${c.icon ? c.icon + " " : ""}${c.label}` : code || "—"}</span>;
}
function CareStatusPill({ status }) {
  const s = STATUSES[status] || STATUSES.open;
  return <span className={`care-pill ${s.cls}`}>{s.label}</span>;
}
function CareAvatar({ name, color = "var(--muted)", size = 22 }) {
  return <span className="care-tk-av" style={{ background: color, width: size, height: size, fontSize: size * 0.42 }}>{initials(name)}</span>;
}

/* ─── MAIN COMPONENT ────────────────────────────────────── */
export default function CustomerCareCRM() {
  const rights = useRights(ticket_PATH);
  const nowRef = useRef(new Date());
  const NOW = nowRef.current;

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [kpis, setKpis] = useState({ open: 0, progress: 0, pending: 0, resolvedToday: 0 });

  const [curId, setCurId] = useState(null);
  const [curTicket, setCurTicket] = useState(null);   // full detail incl. Messages, fetched on select
  const [curLoading, setCurLoading] = useState(false);

  const [curTab, setCurTab] = useState("all");
  const [composeMode, setComposeMode] = useState("reply");
  const [replyText, setReplyText] = useState("");

  const [search, setSearch] = useState("");
  const [filterPrio, setFilterPrio] = useState("");
  const [filterCat, setFilterCat] = useState("");

  const [openDD, setOpenDD] = useState(null);

  const [toasts, setToasts] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newTicket, setNewTicket] = useState(NEW_TICKET_BLANK);
  const [newErrors, setNewErrors] = useState({});
  const cnrDebounceRef = useRef(null);
  const [cnrLookupBusy, setCnrLookupBusy] = useState(false);

  const [customers, setCustomers] = useState([]);        // /dropdown?mode=PMS
  const [priorities, setPriorities] = useState([]);       // /dropdown?mode=PRI
  const [agents, setAgents] = useState([]);               // /api/customercare/agents
  const [mastersLoading, setMastersLoading] = useState(true);
  const [categories, setCategories] = useState([]);       // /dropdown?mode=TicCat

useEffect(() => {
  let cancelled = false;
  setMastersLoading(true);
  Promise.all([
    api.get("/dropdown?mode=PMS"),
    api.get("/dropdown?mode=PRI"),
    api.get("/dropdown?mode=TicCat"),          // ← add this
    api.get("/api/customercare/agents"),
  ])
    .then(([pmsRes, priRes, catRes, agentsRes]) => {   // ← add catRes
      if (cancelled) return;
      setCustomers((pmsRes.data || []).map(r => ({ id: r.Party_id, name: r.Party_Name })));
      setPriorities((priRes.data || []).map(r => ({ id: r.Priority_ID, value: r.Priority_Value })));
      setCategories((catRes.data || []).map(r => ({    // ← add this
        id: r.Ticket_Category_Id, label: r.Ticket_Category_Name, icon: r.CategoryIconEmoji
      })));
      setAgents((agentsRes.data || []).map(r => ({ id: r.emp_Code, name: r.emp_Name })));
    })
    .catch(() => { if (!cancelled) addToast("Could not load master data (customers/priority/categories/agents)", "err"); })
    .finally(() => { if (!cancelled) setMastersLoading(false); });
  return () => { cancelled = true; };
}, []);

  function addToast(msg, type = "info") {
    const id = Date.now() + Math.random();
    setToasts(ts => [...ts, { id, msg, type }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 2600);
  }

  /* ── Load master data once ── */
  useEffect(() => {
    let cancelled = false;
    setMastersLoading(true);
    Promise.all([
      api.get("/dropdown?mode=PMS"),
      api.get("/dropdown?mode=PRI"),
      api.get("/api/customercare/agents"),
    ])
      .then(([pmsRes, priRes, agentsRes]) => {
        if (cancelled) return;
        setCustomers((pmsRes.data || []).map(r => ({ id: r.Party_id, name: r.Party_Name })));
        setPriorities((priRes.data || []).map(r => ({ id: r.Priority_ID, value: r.Priority_Value })));
        setAgents((agentsRes.data || []).map(r => ({ id: r.emp_Code, name: r.emp_Name })));
      })
      .catch(() => { if (!cancelled) addToast("Could not load master data (customers/priority/agents)", "err"); })
      .finally(() => { if (!cancelled) setMastersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /* ── Fetch ticket list (server-side filtered) ── */
  function fetchTickets() {
    setTicketsLoading(true);
    const params = {};
    if (curTab !== "all") params.status = curTab;
    if (filterCat) params.category = filterCat;
    if (filterPrio) params.priorityId = filterPrio;
    if (search.trim()) params.search = search.trim();
    params.pageSize = 200;

    api.get("/api/customercare", { params })
      .then(res => setTickets(res.data?.data || []))
      .catch(() => addToast("Could not load tickets", "err"))
      .finally(() => setTicketsLoading(false));
  }

  function fetchKpis() {
    api.get("/api/customercare/kpis")
      .then(res => setKpis({
        open: res.data?.open ?? 0,
        progress: res.data?.progress ?? 0,
        pending: res.data?.pending ?? 0,
        resolvedToday: res.data?.resolvedToday ?? 0,
      }))
      .catch(() => {});
  }
//
  function fetchTicketDetail(id) {
    setCurLoading(true);
    api.get(`/api/customercare/${id}`)
      .then(res => setCurTicket(res.data))
      .catch(() => addToast("Could not load ticket detail", "err"))
      .finally(() => setCurLoading(false));
  }

  useEffect(() => { fetchTickets(); fetchKpis(); /* eslint-disable-next-line */ }, [curTab, filterCat, filterPrio]);
  useEffect(() => {
    const tmr = setTimeout(fetchTickets, 300); // small debounce for the search box
    return () => clearTimeout(tmr);
    /* eslint-disable-next-line */
  }, [search]);
  useEffect(() => { if (curId) fetchTicketDetail(curId); }, [curId]);

  const overdueCount = useMemo(() => tickets.filter(t => slaInfo(t, NOW).breach).length, [tickets, NOW]);

  function selectTicket(id) { setCurId(id); setOpenDD(null); }

  function refreshAfterMutation(id) {
    fetchTickets();
    fetchKpis();
    if (id) fetchTicketDetail(id);
  }

  // Quick single-click resolve action (deliberate one-click confirm, not a
  // multi-item select list) — still saves immediately, now through the
  // same consolidated /update endpoint as the detail panel's Submit.
  function setStatus(id, status) {
    api.post(`/api/customercare/${id}/update`, { hasStatus: true, status })
      .then(res => {
        addToast("Status → " + (STATUSES[status]?.label || status), status === "resolved" ? "ok" : "info");
        if (status === "closed") {
          addToast(
            res.data?.WhatsApp_Sent ? "WhatsApp notification sent to customer" : "Ticket closed — WhatsApp notification could not be sent",
            res.data?.WhatsApp_Sent ? "ok" : "warn"
          );
        }
        refreshAfterMutation(id);
      })
      .catch(() => addToast("Could not update status", "err"));
    setOpenDD(null);
  }

  // Status / Assign / Priority are picked from dropdowns in the UI, and the
  // composer holds a note/reply — but there's ONE Submit button for all of
  // it. This sends everything staged in a single request, so the backend
  // logs ONE Care_Ticket_Message row per edit instead of one row per field.
  function submitTicketUpdate(id, changes, messageType, text) {
    const txt = (text || "").trim();
    const body = {
      hasStatus: "status" in changes, status: changes.status ?? null,
      hasAgent: "agentId" in changes, agentId: changes.agentId ?? null,
      hasPriority: "priorityId" in changes, priorityId: changes.priorityId ?? null,
      messageType: txt ? messageType : null,
      text: txt || null,
    };

    api.post(`/api/customercare/${id}/update`, body)
      .then(res => {
        addToast("Ticket updated", "ok");
        if (changes.status === "closed") {
          addToast(
            res.data?.WhatsApp_Sent ? "WhatsApp notification sent to customer" : "Ticket closed — WhatsApp notification could not be sent",
            res.data?.WhatsApp_Sent ? "ok" : "warn"
          );
        }
        setReplyText("");
        refreshAfterMutation(id);
      })
      .catch(() => addToast("Could not save changes", "err"));
    setOpenDD(null);
  }

  function insertCanned(i) { setReplyText(v => (v ? v + "\n" : "") + CANNED[i]); }

  function openNewModal() {
    // setNewTicket(NEW_TICKET_BLANK);
    setNewTicket({ ...NEW_TICKET_BLANK, category: categories[0]?.id || "" });
    setNewErrors({});
    setShowNewModal(true);
  }

  function lookupCnr(overrideCnr) {
    const cnr = (overrideCnr ?? newTicket.cnrInput).trim();
    if (!cnr) return;
    setCnrLookupBusy(true);
    api.get("/api/customercare/consignment-lookup", { params: { cnr } })
      .then(res => {
        setNewTicket(nt => ({ ...nt, consignmentId: res.data.consignmentId, route: res.data.route || "" }));
        addToast("Consignment found", "ok");
      })
      .catch(() => {
        setNewTicket(nt => ({ ...nt, consignmentId: null, route: "" }));
        addToast("No consignment found for that number", "warn");
      })
      .finally(() => setCnrLookupBusy(false));
  }

  function saveNewTicket() {
    const errs = {};
    if (!newTicket.partyId) errs.partyId = "Select a customer";
    if (!newTicket.subject.trim()) errs.subject = "Subject is required";
    if (!newTicket.desc.trim()) errs.desc = "Description is required";
    if (Object.keys(errs).length) { setNewErrors(errs); addToast("Please fill the required fields", "warn"); return; }

    api.post("/api/customercare", {
      Party_Id: Number(newTicket.partyId),
      Contact_Person: newTicket.contact.trim() || null,
      Contact_Phone: newTicket.phone.replace(/\D/g, "") || null,
      Contact_Email: newTicket.email.trim() || null,
      Channel: newTicket.channel,
      Category: newTicket.category,
      Priority_ID: newTicket.priorityId ? Number(newTicket.priorityId) : null,
      Consignment_Id: newTicket.consignmentId,
      Agent_Id: newTicket.agentId ? Number(newTicket.agentId) : null,
      Subject: newTicket.subject.trim(),
      Description: newTicket.desc.trim(),
    })
      .then(res => {
        setShowNewModal(false);
        addToast("Ticket " + (res.data.Ticket_No || res.data.ticket_No) + " created", "ok");
        setCurTab("all");
        fetchTickets();
        fetchKpis();
        setCurId(res.data.Ticket_Id ?? res.data.ticket_Id);
      })
      .catch(() => addToast("Could not create ticket", "err"));
  }

  useEffect(() => {
    function onDocClick() { setOpenDD(null); }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <div className="care-root">
      <div className="care-page">
      <div className="care-page-header">
        <div>
          <div className="care-page-title">Customer Care <span>CRM</span></div>
          <div className="care-page-sub">Track customer complaints, queries &amp; service requests — with SLA, ownership and full conversation history.</div>
        </div>
       {rights.add && (<button className="care-btn care-btn-primary" onClick={openNewModal}>+ New Ticket</button>)}
      </div>

      {/* KPI Strip */}
      <div className="care-kpi-row">
        <div className="care-kpi"><div className="care-kpi-val">{kpis.open}</div><div className="care-kpi-lbl">Open</div></div>
        <div className="care-kpi care-k-prog"><div className="care-kpi-val">{kpis.progress}</div><div className="care-kpi-lbl">In Progress</div></div>
        <div className="care-kpi care-k-pend"><div className="care-kpi-val">{kpis.pending}</div><div className="care-kpi-lbl">Pending Customer</div></div>
        <div className="care-kpi care-k-over"><div className="care-kpi-val">{overdueCount}</div><div className="care-kpi-lbl">SLA Overdue</div></div>
        <div className="care-kpi care-k-res"><div className="care-kpi-val">{kpis.resolvedToday}</div><div className="care-kpi-lbl">Resolved Today</div></div>
      </div>

      <div className="care-crm">
        {/* ─── LIST PANE ─── */}
        <div className="care-card">
          <div className="care-list-hd">
            <div className="care-search">
              <span>🔍</span>
              <input placeholder="Search ticket, customer, phone…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="care-filt-row">
              <CareComboBox small wrapClass="care-cb-grow" value={filterPrio} onChange={setFilterPrio}
                emptyLabel="All Priority" placeholder="Search priority…"
                options={priorities.map(p => ({ value: p.id, label: p.value }))} />
              <CareComboBox small wrapClass="care-cb-grow" value={filterCat} onChange={setFilterCat}
                emptyLabel="All Categories" placeholder="Search category…"
                options={categories.map(c => ({ value: c.id, label: (c.icon ? c.icon + " " : "") + c.label }))} />
            </div>
          </div>

          <div className="care-tabs">
            {TAB_DEFS.map(([k, l]) => (
              <div key={k} className={`care-tab${curTab === k ? " active" : ""}`} onClick={() => setCurTab(k)}>
                {l}{k === curTab && <span className="care-cnt">{tickets.length}</span>}
              </div>
            ))}
          </div>

          <div className="care-tk-list">
            {ticketsLoading ? (
              <div className="care-tk-empty">Loading tickets…</div>
            ) : tickets.length === 0 ? (
              <div className="care-tk-empty">No tickets match</div>
            ) : tickets.map(t => {
              const sla = slaInfo(t, NOW);
              return (
                <div key={t.ticket_Id} className={`care-tk${curId === t.ticket_Id ? " active" : ""}`} onClick={() => selectTicket(t.ticket_Id)}>
                  <div className="care-tk-top">
                    <span className={`care-prio-dot care-p-${(t.priority_Value || "").toLowerCase()}`} />
                    <span className="care-tk-no">{t.ticket_No}</span>
                    <span className="care-tk-time">{relTime(t.created_Date, NOW)}</span>
                  </div>
                  <div className="care-tk-subj">{t.contact_Person || t.party_Name || "—"}</div>
                  <div className="care-tk-meta">
                    <CareCatChip code={t.category} categories={categories} />
                    <CareStatusPill status={t.status} />
                  </div>
                  <div className="care-tk-meta" style={{ marginTop: 7 }}>
                    {t.agent_Name ? <CareAvatar name={t.agent_Name} color="var(--accent2)" /> : <span className="care-tk-av" style={{ background: "var(--muted)" }}>–</span>}
                    <span className="care-tk-cust">{t.party_Name || t.party_Id}</span>
                    <span className={`care-tk-sla ${sla.cls}`}>{sla.breach ? "⚠ " : ""}{sla.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── DETAIL PANE ─── */}
        <div className="care-card">
          {!curId ? (
            <div className="care-detail-empty">
              <div className="care-e-ico">🎧</div>
              <div className="care-e-title">Select a ticket</div>
              <div>Pick a ticket on the left to view the conversation and take action</div>
            </div>
          ) : curLoading || !curTicket ? (
            <div className="care-detail-empty"><div className="care-e-title">Loading…</div></div>
          ) : (
            <DetailPane
              t={curTicket}
              now={NOW}
              agents={agents}
              priorities={priorities}
              categories={categories}
              openDD={openDD}
              setOpenDD={setOpenDD}
              onSetStatus={setStatus}
              onSubmit={submitTicketUpdate}
              composeMode={composeMode}
              setComposeMode={setComposeMode}
              replyText={replyText}
              setReplyText={setReplyText}
              onInsertCanned={insertCanned}
              onNothingToSubmit={() => addToast("Nothing to submit — change a field or type a message", "warn")}
            />
          )}
        </div>
      </div>

      {/* ─── NEW TICKET MODAL ─── */}
      {showNewModal && (
        <div className="care-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowNewModal(false); }}>
          <div className="care-modal">
            <div className="care-modal-head">
              <div className="care-mh-ico">📩</div>
              <div className="care-mh-title">New Support Ticket</div>
              <button className="care-modal-x" onClick={() => setShowNewModal(false)}>✕</button>
            </div>
            <div className="care-modal-body">
              <div className="care-g2">
                <div className="care-field">
                  <label>Customer <span className="care-req">*</span></label>
                  <CareComboBox value={newTicket.partyId} disabled={mastersLoading} hasError={!!newErrors.partyId}
                    onChange={v => setNewTicket({ ...newTicket, partyId: v })}
                    emptyLabel={mastersLoading ? "Loading…" : "— Select —"} placeholder="Search customer…"
                    options={customers.map(c => ({ value: c.id, label: c.name }))} />
                  {newErrors.partyId && <div className="care-err-text">{newErrors.partyId}</div>}
                </div>
                <div className="care-field">
                  <label>Contact Person</label>
                  <input placeholder="Caller name" value={newTicket.contact} onChange={e => setNewTicket({ ...newTicket, contact: e.target.value })} />
                </div>
              </div>
              <div className="care-g2">
                <div className="care-field">
                  <label>Phone</label>
                  <input className="care-mono" placeholder="+91 …" value={newTicket.phone} onChange={e => setNewTicket({ ...newTicket, phone: e.target.value })} />
                </div>
                <div className="care-field">
                  <label>Email</label>
                  <input placeholder="name@company.com" value={newTicket.email} onChange={e => setNewTicket({ ...newTicket, email: e.target.value })} />
                </div>
              </div>
              <div className="care-g3">
                {/* <div className="care-field">
                  <label>Channel</label>
                  <select className="care-select" value={newTicket.channel} onChange={e => setNewTicket({ ...newTicket, channel: e.target.value })}>
                    {["Phone", "Email", "Web Portal", "WhatsApp"].map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div> */}
                <div className="care-field">
                  <label>Category</label>
                  <CareComboBox value={newTicket.category} onChange={v => setNewTicket({ ...newTicket, category: v })}
                    placeholder="Search category…"
                    options={categories.map(c => ({ value: c.id, label: (c.icon ? c.icon + " " : "") + c.label }))} />
                </div>
                <div className="care-field">
                  <label>Priority</label>
                  <CareComboBox value={newTicket.priorityId} disabled={mastersLoading}
                    onChange={v => setNewTicket({ ...newTicket, priorityId: v })}
                    emptyLabel={mastersLoading ? "Loading…" : "— Select —"} placeholder="Search priority…"
                    options={priorities.map(p => ({ value: p.id, label: p.value }))} />
                </div>
                <div className="care-field">
                  <label>Assign To</label>
                  <CareComboBox value={newTicket.agentId} disabled={mastersLoading}
                    onChange={v => setNewTicket({ ...newTicket, agentId: v })}
                    emptyLabel="Unassigned" placeholder="Search agent…"
                    options={agents.map(a => ({ value: a.id, label: a.name }))} />
                </div>
              </div>
              <div className="care-g2">
                <div className="care-field">
                  <label>Consignment No (CNR)</label>
                  <input className="care-mono" placeholder="e.g. 202607000921" value={newTicket.cnrInput}
                    onChange={e => {
                      const val = e.target.value;
                      setNewTicket(nt => ({ ...nt, cnrInput: val, consignmentId: null, route: "" }));
                      clearTimeout(cnrDebounceRef.current);
                      cnrDebounceRef.current = setTimeout(() => lookupCnr(val), 500);
                    }}
                    onBlur={() => lookupCnr()} />
                  {cnrLookupBusy && <div className="care-pin-hint">Looking up consignment…</div>}
                  {!cnrLookupBusy && newTicket.route && <div className="care-pin-hint">Route: {newTicket.route}</div>}
                </div>
                <div className="care-field">
                <label>Subject <span className="care-req">*</span></label>
                <input placeholder="Short summary" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} />
                {newErrors.subject && <div className="care-err-text">{newErrors.subject}</div>}
              </div>
              </div>
              
              <div className="care-field">
                <label>Description <span className="care-req">*</span></label>
                <textarea placeholder="Describe the customer's issue or request…" value={newTicket.desc} onChange={e => setNewTicket({ ...newTicket, desc: e.target.value })} />
                {newErrors.desc && <div className="care-err-text">{newErrors.desc}</div>}
              </div>
            </div>
            <div className="care-modal-foot">
              <button className="care-btn care-btn-ghost" onClick={() => setShowNewModal(false)}>Cancel</button>
              <button className="care-btn care-btn-green" disabled={cnrLookupBusy} onClick={saveNewTicket}>{cnrLookupBusy ? "Checking consignment…" : "✓ Create Ticket"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TOASTS ─── */}
      <div className="care-toast-wrap">
        {toasts.map(t => (
          <div key={t.id} className={`care-toast care-toast-${t.type}`}>
            <span>{{ ok: "✓", err: "✕", warn: "⚠", info: "ℹ" }[t.type] || "ℹ"}</span>{t.msg}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

/* ─── DETAIL PANE SUBCOMPONENT ──────────────────────────── */
function DetailPane({ t, now, agents, priorities, categories, openDD, setOpenDD, onSetStatus, onSubmit, composeMode, setComposeMode, replyText, setReplyText, onInsertCanned, onNothingToSubmit }) {
  const rights = useRights(ticket_PATH);
  const sla = slaInfo(t, now);
  const isOpenOrClosed = t.status === "resolved" || t.status === "closed";
  // Closed tickets are permanently read-only — no status/assign/priority
  // changes, no replies, no notes. (Resolved is not locked; only closed is.)
  const isClosed = t.status === "closed";
  const threadRef = useRef(null);
  const messages = t.messages || [];

  // ── Staged (draft) Status / Assign / Priority selection ──
  // Picking an item in these dropdowns no longer saves right away — it only
  // updates this local draft. Nothing is sent to the server (and nothing is
  // logged on the ticket) until the composer's Submit button is clicked, so
  // a misclick followed by the correct pick never writes the wrong value.
  const [draft, setDraft] = useState({
    status: t.status,
    agentId: t.agent_Id ?? "",
    priorityId: t.priority_ID ?? "",
  });

  useEffect(() => {
    setDraft({ status: t.status, agentId: t.agent_Id ?? "", priorityId: t.priority_ID ?? "" });
  }, [t.ticket_Id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    draft.status !== t.status ||
    String(draft.agentId) !== String(t.agent_Id ?? "") ||
    String(draft.priorityId) !== String(t.priority_ID ?? "");

  function pickStatus(k) { if (isClosed) return; setDraft(d => ({ ...d, status: k })); setOpenDD(null); }
  function pickAgent(id) { if (isClosed) return; setDraft(d => ({ ...d, agentId: id ?? "" })); setOpenDD(null); }
  function pickPriority(id) { if (isClosed) return; setDraft(d => ({ ...d, priorityId: id })); setOpenDD(null); }

  // Everything staged (Status / Assign / Priority picks AND the composer
  // text) is committed by ONE Submit button, in ONE request — the backend
  // logs it as a single Care_Ticket_Message row instead of one per field.
  function handleSubmit() {
    if (isClosed) return;
    const changes = {};
    if (draft.status !== t.status) changes.status = draft.status;
    if (String(draft.agentId) !== String(t.agent_Id ?? "")) changes.agentId = draft.agentId || null;
    if (String(draft.priorityId) !== String(t.priority_ID ?? "")) changes.priorityId = draft.priorityId ? Number(draft.priorityId) : null;

    const txt = replyText.trim();
    const hasChanges = Object.keys(changes).length > 0;

    if (!hasChanges && !txt) {
      onNothingToSubmit();
      return;
    }
    onSubmit(t.ticket_Id, changes, composeMode === "note" ? "note" : "agent", txt);
  }

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages.length]);

  function toggleDD(name, e) {
    e.stopPropagation();
    if (isClosed) return;
    setOpenDD(d => (d === name ? null : name));
  }

  return (
    <>
      <div className="care-dt-hd">
        <div className="care-dt-hd-top">
          <span className="care-dt-no">{t.ticket_No}</span>
          <CareStatusPill status={t.status} />
          <CareCatChip code={t.category} categories={categories} />
          <span className="care-prio-text">{t.priority_Value || "—"}</span>
        </div>
        <div className="care-dt-subj">{t.contact_Person || t.party_Name}</div>
        {isClosed && (
          <div className="care-closed-banner">🔒 This ticket is closed. All fields are read-only and can no longer be edited.</div>
        )}
        <div className="care-dt-actions">
          <div className="care-dd">
            <button className="care-btn care-btn-ghost care-btn-sm" disabled={isClosed} onClick={e => toggleDD("status", e)}>Status: {STATUSES[draft.status]?.label || draft.status} ▾</button>
            {openDD === "status" && (
              <div className="care-dd-menu" onMouseDown={e => e.stopPropagation()}>
                {Object.entries(STATUSES).map(([k, s]) => (
                  <div key={k} className="care-dd-item" onClick={() => pickStatus(k)}>{k === draft.status ? "✓ " : ""}<span className={`care-pill ${s.cls}`}>{s.label}</span></div>
                ))}
              </div>
            )}
          </div>
          {/* <div className="care-dd">
            <button className="care-btn care-btn-ghost care-btn-sm" disabled={isClosed} onClick={e => toggleDD("agent", e)}>Assign: {agents.find(a => String(a.id) === String(draft.agentId))?.name || "Unassigned"} ▾</button>
            {openDD === "agent" && (
              <div className="care-dd-menu" onMouseDown={e => e.stopPropagation()}>
                {agents.map(a => (
                  <div key={a.id} className="care-dd-item" onClick={() => pickAgent(a.id)}>{String(a.id) === String(draft.agentId) ? "✓ " : ""}<CareAvatar name={a.name} color="var(--accent2)" size={18} />{a.name}</div>
                ))}
                <div className="care-dd-item" onClick={() => pickAgent(null)}>{!draft.agentId ? "✓ " : ""}Unassign</div>
              </div>
            )}
          </div> */}
          <CareComboBox small wrapClass="care-cb-act" value={draft.agentId} disabled={isClosed}
              onChange={v => pickAgent(v === "" ? null : agents.find(a => String(a.id) === v)?.id)}
              emptyLabel="Unassigned" placeholder="Search agent…"
              options={agents.map(a => ({ value: a.id, label: a.name }))}
           />
          <div className="care-dd">
            <button className="care-btn care-btn-ghost care-btn-sm" disabled={isClosed} onClick={e => toggleDD("prio", e)}>Priority: {priorities.find(p => String(p.id) === String(draft.priorityId))?.value || "—"} ▾</button>
            {openDD === "prio" && (
              <div className="care-dd-menu" onMouseDown={e => e.stopPropagation()}>
                {priorities.map(p => (
                  <div key={p.id} className="care-dd-item" onClick={() => pickPriority(p.id)}>{String(p.id) === String(draft.priorityId) ? "✓ " : ""}<span className={`care-prio-dot care-p-${(p.value || "").toLowerCase()}`} />{p.value}</div>
                ))}
              </div>
            )}
          </div>
          {!isDirty && !isOpenOrClosed && <button className="care-btn care-btn-green care-btn-sm" onClick={() => onSetStatus(t.ticket_Id, "resolved")}>✓ Resolve</button>}
        </div>
      </div>

      <div className="care-info-grid">
        <div className="care-info-cell"><div className="care-info-lbl">Customer</div><div className="care-info-val">{t.party_Name || t.party_Id}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Contact</div><div className="care-info-val">{t.contact_Person || "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Phone</div><div className="care-info-val">{t.contact_Phone ? <a href={`tel:${t.contact_Phone}`}>+91 {t.contact_Phone}</a> : "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Email</div><div className="care-info-val">{t.contact_Email ? <a href={`mailto:${t.contact_Email}`}>{t.contact_Email}</a> : "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Channel</div><div className="care-info-val">{t.channel || "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Consignment</div><div className="care-info-val">{t.consignment_No || "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Route</div><div className="care-info-val">{t.route || "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Branch</div><div className="care-info-val">{t.branch_Name || "—"}</div></div>
        <div className="care-info-cell"><div className="care-info-lbl">Owner</div><div className="care-info-val">{t.agent_Name ? <><CareAvatar name={t.agent_Name} color="var(--accent2)" size={20} />{t.agent_Name}</> : <span style={{ color: "var(--muted)" }}>Unassigned</span>}</div></div>
      </div>

      <div className="care-sla-bar">
        <div className="care-info-lbl" style={{ margin: 0 }}>SLA</div>
        <div className="care-sla-track"><div className="care-sla-fill" style={{ width: sla.pct + "%", background: sla.breach ? "var(--danger)" : (sla.cls === "care-sla-warn" ? "var(--warn)" : "var(--success)") }} /></div>
        <span className={`care-sla-text ${sla.cls}`}>{sla.breach ? "⚠ " : ""}{sla.label}</span>
        <span className="care-sla-meta">Opened {fmtDT(t.created_Date)} · Target {fmtDT(sla.due)}</span>
      </div>

      <div className="care-thread" ref={threadRef}>
        {messages.map(m => {
          // Legacy rows from before consolidation — still rendered as before.
          if (m.message_Type === "event") return <div key={m.message_Id} className="care-evt"><span>{m.message_Text} · {fmtDT(m.created_Date)}</span></div>;
          const isCust = m.message_Type === "customer";
          const color = isCust ? "var(--accent2)" : "var(--success)";
          const roleLbl = isCust ? <span className="care-msg-role care-role-cust">Customer</span> : (m.message_Type === "note" ? <span className="care-msg-role care-role-note">Internal</span> : <span className="care-msg-role care-role-agent">Agent</span>);
          // Consolidated edit rows carry Status/owner as columns, not text —
          // surface them as a compact meta line above the note/reply (if any).
          const metaBits = [];
          if (!isCust && m.status) metaBits.push(`Status: ${STATUSES[m.status]?.label || m.status}`);
          // Agent/Internal rows: whoever the ticket was handed to at that point
          // (Author_Name, resolved from Author_Id -> Employee). Customer rows:
          // the ticket's current owner (t.agent_Name, resolved from
          // Care_Ticket.Agent_Id -> Employee).
          const ownerLine = isCust
            ? (t.agent_Name ? `Assigned to ${t.agent_Name}` : null)
            : (m.author_Name ? `Reassigned to ${m.author_Name}` : null);
          if (ownerLine) metaBits.push(ownerLine);
          return (
            <div key={m.message_Id} className={`care-msg care-msg-${m.message_Type}`}>
              <div className="care-msg-av" style={{ background: color }}>{initials(m.author_Name)}</div>
              <div className="care-msg-body">
                <div className="care-msg-head"><span className="care-msg-auth">{m.author_Name || "—"}</span>{roleLbl}<span className="care-msg-at">{fmtDT(m.created_Date)}</span></div>
                {metaBits.length > 0 && <div style={{ fontSize: 12, color: "var(--muted)", margin: "2px 0 4px" }}>{metaBits.join(" · ")}</div>}
                {m.message_Text && <div className="care-msg-text">{m.message_Text}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {isClosed ? (
        <div className="care-composer care-composer-closed">
          <span className="care-comp-hint">🔒 This ticket is closed — replies and notes can no longer be added.</span>
        </div>
      ) : (
        <div className={`care-composer${composeMode === "note" ? " care-note-mode" : ""}`}>
          <div className="care-comp-seg">
            <button className={composeMode === "reply" ? "on" : ""} onClick={() => setComposeMode("reply")}>💬 Public Reply</button>
            <button className={composeMode === "note" ? "on care-note-on" : ""} onClick={() => setComposeMode("note")}>📝 Internal Note</button>
          </div>
          <div className="care-canned">
            {CANNED.map((c, i) => <span key={i} className="care-canned-chip" onClick={() => onInsertCanned(i)}>{c.slice(0, 32)}…</span>)}
          </div>
          <textarea
            placeholder={composeMode === "note" ? "Add an internal note (not visible to customer)…" : "Type your reply to the customer…"}
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
          />
          <div className="care-comp-foot">
            <span className="care-comp-hint">{composeMode === "note" ? "Internal — visible to agents only" : "Public — visible to the customer"}</span>
           {rights.add && (<button className={`care-btn ${composeMode === "note" ? "care-btn-teal" : "care-btn-primary"} care-btn-sm`} onClick={handleSubmit}>➤ Submit</button>)}
          </div>
        </div>
      )}
    </>
  );
}